import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import { digitalHumansApi } from '@/services/api';
import { useOrgContextStore } from '@/stores/orgContextStore';
import { invalidateDigitalHumans } from './useDigitalHumans';
import {
  DIGITAL_HUMAN_VERIFICATION_STATUS,
  FACE_VERIFICATION_SESSION_STATE,
  PRIMARY_ASSET_VERIFICATION_STATUS,
} from '@/types/digital-human';
import {
  getVerificationInput,
  hasEnteredPrimaryImageStage,
} from '@/components/features/ai-toolbox/digital-human-verification/verification-step';
import type {
  DigitalHumanVerificationRequest,
  DigitalHumanVerificationStatusResponse,
} from '@/types/digital-human';

const digitalHumanVerificationKeys = {
  all: ['digital-human-verification'] as const,
  context: (activeOrgId: string | null) =>
    [...digitalHumanVerificationKeys.all, activeOrgId ?? 'personal'] as const,
  status: (activeOrgId: string | null, digitalHumanId: string) =>
    [...digitalHumanVerificationKeys.context(activeOrgId), 'status', digitalHumanId] as const,
};

export function isDigitalHumanVerificationTerminal(
  status?: DigitalHumanVerificationStatusResponse | null
): boolean {
  if (!status) return false;
  if (status.step === 'verification_completed') return true;
  const inPrimaryImageStage = hasEnteredPrimaryImageStage(getVerificationInput(status));
  if (
    !inPrimaryImageStage &&
    (status.isExpired || status.sessionState === FACE_VERIFICATION_SESSION_STATE.EXPIRED)
  ) {
    return true;
  }
  if (status.sessionState === FACE_VERIFICATION_SESSION_STATE.FAILED) return true;
  if (status.step === 'bio_verification_failed' || status.step === 'primary_image_failed')
    return true;
  return (
    status.verificationStatus === DIGITAL_HUMAN_VERIFICATION_STATUS.VERIFIED &&
    status.primaryAssetVerificationStatus === PRIMARY_ASSET_VERIFICATION_STATUS.VERIFIED
  );
}

export function invalidateDigitalHumanVerification(
  queryClient: QueryClient,
  digitalHumanId?: string | null
) {
  if (digitalHumanId) {
    void queryClient.invalidateQueries({
      queryKey: [...digitalHumanVerificationKeys.all],
      predicate: (query) => query.queryKey.includes(digitalHumanId),
    });
  } else {
    void queryClient.invalidateQueries({ queryKey: digitalHumanVerificationKeys.all });
  }
  invalidateDigitalHumans(queryClient);
}

export function useStartDigitalHumanVerification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      digitalHumanId,
      data,
    }: {
      digitalHumanId: string;
      data?: DigitalHumanVerificationRequest;
    }) => digitalHumansApi.startVerification(digitalHumanId, data),
    onSuccess: (_, variables) => {
      invalidateDigitalHumanVerification(queryClient, variables.digitalHumanId);
    },
  });
}

export function useDigitalHumanVerificationCreditPolicy() {
  return useQuery({
    queryKey: [...digitalHumanVerificationKeys.all, 'credit-policy'],
    queryFn: () => digitalHumansApi.getVerificationCreditPolicy(),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 10,
  });
}

export function useDigitalHumanVerificationStatus(
  digitalHumanId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQuery({
    queryKey: digitalHumanId
      ? digitalHumanVerificationKeys.status(activeOrgId, digitalHumanId)
      : [...digitalHumanVerificationKeys.context(activeOrgId), 'status', 'disabled'],
    queryFn: () => {
      if (!digitalHumanId) throw new Error('Missing digitalHumanId');
      return digitalHumansApi.getVerificationStatus(digitalHumanId);
    },
    enabled: (options?.enabled ?? true) && !!digitalHumanId,
    refetchInterval: (query) => {
      const data = query.state.data as DigitalHumanVerificationStatusResponse | undefined;
      if (isDigitalHumanVerificationTerminal(data)) return false;
      return options?.refetchInterval ?? 3000;
    },
    staleTime: 0,
    gcTime: 1000 * 60 * 5,
  });
}

export function useDigitalHumanVerificationStatuses(
  digitalHumanIds: string[],
  options?: {
    enabled?: boolean;
    staleTime?: number;
  }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);

  return useQueries({
    queries: digitalHumanIds.map((digitalHumanId) => ({
      queryKey: digitalHumanVerificationKeys.status(activeOrgId, digitalHumanId),
      queryFn: () => digitalHumansApi.getVerificationStatus(digitalHumanId),
      enabled: (options?.enabled ?? true) && Boolean(digitalHumanId),
      staleTime: options?.staleTime ?? 1000 * 30,
      gcTime: 1000 * 60 * 5,
    })),
  });
}
